import {
  PortalArticleBlock,
  PortalArticleQuickSummary,
  PortalArticleSection,
  PortalArticleSectionContentItem,
  PortalArticleTopicBlock,
  PortalArticleTopicItem,
} from '../../../models/PortalArtigoModel'
import { clonePortalTable, createDefaultPortalTable } from './portal-table'

export type PortalArticleBlockType = PortalArticleBlock['type']

export function createDefaultBlock(type: PortalArticleBlockType): PortalArticleBlock {
  const id = createPortalEditorBlockId()

  if (type === 'summary') return { type, id, topics: [] }
  if (type === 'topic') return createDefaultTopic()
  return { type: 'intro', id, section: createDefaultSection() }
}

export function cloneBlock(block: PortalArticleBlock) {
  return {
    ...(JSON.parse(JSON.stringify(block)) as PortalArticleBlock),
    id: createPortalEditorBlockId(),
  } as PortalArticleBlock
}

export function createDefaultSection(): PortalArticleSection {
  return {
    id: createPortalEditorBlockId('section'),
    title: '',
    content: [],
  }
}

export function createDefaultTopic(): PortalArticleTopicBlock {
  const id = createPortalEditorBlockId('topic')
  return {
    type: 'topic',
    id,
    items: [createDefaultTopicSectionItem('main')],
  }
}

export function createDefaultTopicSectionItem(
  role: 'main' | 'subtopic'
): Extract<PortalArticleTopicItem, { type: 'section' }> {
  return {
    type: 'section',
    id: createPortalEditorBlockId(role),
    role,
    section: createDefaultSection(),
  }
}

export function createDefaultTopicImageItem(): Extract<PortalArticleTopicItem, { type: 'image' }> {
  return {
    type: 'image',
    id: createPortalEditorBlockId('image'),
    image: { src: '', alt: '', width: 1200, height: 675 },
  }
}

export function createDefaultTopicQuickSummaryItem(): Extract<
  PortalArticleTopicItem,
  { type: 'quickSummary' }
> {
  return {
    type: 'quickSummary',
    id: createPortalEditorBlockId('quick-summary'),
    quickSummary: createDefaultQuickSummary(),
  }
}

export function createDefaultQuickSummary(): PortalArticleQuickSummary {
  return {
    title: 'Resumo rápido:',
    paragraph: '',
    items: [''],
  }
}

export function createDefaultContentItem(
  type: PortalArticleSectionContentItem['type']
): PortalArticleSectionContentItem {
  if (type === 'bullets') return { type, items: [''], listStyle: 'disc' }
  if (type === 'note') return { type, content: '' }
  if (type === 'cards') return { type, items: [] }
  if (type === 'image') {
    return { type, image: { src: '', alt: '', width: 1200, height: 675 } }
  }
  if (type === 'youtube') return { type, videoId: '' }
  if (type === 'table') return createDefaultPortalTable(createPortalEditorBlockId)
  return { type, content: '' }
}

export function cloneContentItem(item: PortalArticleSectionContentItem) {
  if (item.type === 'table') return clonePortalTable(item, createPortalEditorBlockId)
  return JSON.parse(JSON.stringify(item)) as PortalArticleSectionContentItem
}

export function cloneSectionContentWithFreshTableIds(
  content: PortalArticleSectionContentItem[]
) {
  return content.map((item) =>
    item.type === 'table'
      ? clonePortalTable(item, createPortalEditorBlockId)
      : JSON.parse(JSON.stringify(item)) as PortalArticleSectionContentItem
  )
}

export function createPortalEditorBlockId(prefix = 'block') {
  const randomValue =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`

  return `${prefix}-${randomValue}`
}
